import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Printer, Download, Link2, FileText, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api/customs-fillable`;

/**
 * Public fillable customs form. Renders the ORIGINAL form as a background image
 * and overlays input boxes exactly on the blanks. Typed values are sent back to
 * the server which overlays them onto the original PDF (shape untouched) so the
 * customer/staff can print & sign.
 */
export default function CustomsFormFillPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const pkg = searchParams.get("pkg");
  const [layout, setLayout] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [pageDims, setPageDims] = useState({}); // {pageNo: {w, h}}
  const imgRefs = useRef({});

  useEffect(() => {
    document.title = "Fill Customs Form";
    setLoading(true);
    axios
      .get(`${API}/${slug}/layout`)
      .then((res) => setLayout(res.data))
      .catch(() => setError("This form could not be loaded."))
      .finally(() => setLoading(false));
  }, [slug]);

  // When opened from a sent package link, load the pre-filled values.
  useEffect(() => {
    if (!pkg) return;
    axios
      .get(`${API}/package/${pkg}/${slug}`)
      .then((res) => setValues(res.data?.values || {}))
      .catch(() => {/* fall back to blank form */});
  }, [pkg, slug]);

  const measure = useCallback(() => {
    const dims = {};
    Object.entries(imgRefs.current).forEach(([pno, el]) => {
      if (el) {
        const r = el.getBoundingClientRect();
        dims[pno] = { w: r.width, h: r.height };
      }
    });
    setPageDims(dims);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const handleChange = (id, v) => setValues((prev) => ({ ...prev, [id]: v }));

  const generatePdf = async (mode) => {
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/${slug}/pdf`,
        { values },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = `${slug}-filled.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else {
        const w = window.open(url, "_blank");
        // Auto-trigger print dialog once the PDF tab is ready
        if (w) setTimeout(() => { try { w.focus(); w.print(); } catch (e) { /* viewer handles */ } }, 1200);
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error("Could not generate the PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    const link = `${window.location.origin}/customs-form/${slug}`;
    navigator.clipboard.writeText(link).then(() => {
      setLinkCopied(true);
      toast.success("Customer link copied");
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error || !layout) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 text-slate-600">
        {error || "Form not found."}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200 pb-24">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-slate-900 text-white shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="h-5 w-5 text-amber-400 flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-semibold truncate" data-testid="customs-form-title">{layout.title}</h1>
              <p className="text-xs text-slate-300">Type into the highlighted boxes, then print & sign.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary" size="sm" onClick={copyLink}
              className="bg-slate-700 hover:bg-slate-600 text-white border-0"
              data-testid="customs-copy-link-btn"
            >
              {linkCopied ? <Check className="h-4 w-4 mr-1" /> : <Link2 className="h-4 w-4 mr-1" />}
              {linkCopied ? "Copied" : "Copy link"}
            </Button>
            <Button
              variant="secondary" size="sm" onClick={() => generatePdf("download")}
              disabled={generating}
              className="bg-slate-700 hover:bg-slate-600 text-white border-0"
              data-testid="customs-download-pdf-btn"
            >
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button
              size="sm" onClick={() => generatePdf("print")} disabled={generating}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
              data-testid="customs-print-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
              Print / Sign
            </Button>
          </div>
        </div>
      </div>

      {/* Form pages */}
      <div className="max-w-4xl mx-auto px-2 sm:px-4 pt-5 space-y-6">
        {layout.pages.map((pg) => {
          const dim = pageDims[pg.page] || { w: 0, h: 0 };
          return (
            <div
              key={pg.page}
              className="relative bg-white shadow-xl mx-auto"
              style={{ width: "100%" }}
            >
              <img
                ref={(el) => { imgRefs.current[pg.page] = el; }}
                src={`${BACKEND}${pg.image}`}
                alt={`${layout.title} page ${pg.page + 1}`}
                className="block w-full h-auto select-none"
                draggable={false}
                onLoad={measure}
              />
              {pg.fields.map((f) => {
                const fontSize = Math.max(9, (dim.h * f.h) / 100 * 0.82);
                if (f.kind === "checkbox") {
                  const checked = !!values[f.id];
                  return (
                    <button
                      key={f.id}
                      type="button"
                      title={f.label || "Tick"}
                      onClick={() => handleChange(f.id, checked ? "" : "X")}
                      data-testid={`customs-field-${f.id}`}
                      style={{
                        position: "absolute",
                        left: `${f.x - f.w * 0.15}%`,
                        top: `${f.y - f.h * 0.15}%`,
                        width: `${Math.max(f.w * 1.4, 1.1)}%`,
                        height: `${Math.max(f.h * 1.1, 1.3)}%`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: `${fontSize * 1.1}px`,
                        lineHeight: 1,
                        fontWeight: 700,
                        color: "#0b3d91",
                        background: checked
                          ? "rgba(11, 61, 145, 0.12)"
                          : "rgba(255, 235, 130, 0.45)",
                        border: "none",
                        outline: "none",
                        cursor: "pointer",
                        borderRadius: 2,
                        padding: 0,
                      }}
                    >
                      {checked ? "✕" : ""}
                    </button>
                  );
                }
                return (
                  <input
                    key={f.id}
                    type="text"
                    title={f.label || "Fill in"}
                    value={values[f.id] || ""}
                    onChange={(e) => handleChange(f.id, e.target.value)}
                    data-testid={`customs-field-${f.id}`}
                    spellCheck={false}
                    style={{
                      position: "absolute",
                      left: `${f.x}%`,
                      top: `${f.y - f.h * 0.15}%`,
                      width: `${f.w}%`,
                      height: `${Math.max(f.h * 1.4, 1.6)}%`,
                      fontSize: `${fontSize}px`,
                      lineHeight: 1,
                      color: "#0b3d91",
                      fontWeight: 500,
                      background: "rgba(255, 235, 130, 0.35)",
                      border: "none",
                      outline: "none",
                      padding: "0 2px",
                      borderRadius: 2,
                    }}
                  />
                );
              })}
            </div>
          );
        })}
        <p className="text-center text-xs text-slate-500 pb-6">
          Filling in this form does not submit it to customs — after printing, please sign/stamp where required.
        </p>
      </div>
    </div>
  );
}
